"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Mail, MapPin, Phone } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactDetailsAndForm() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section ref={sectionRef} className="bg-light-background py-16 px-4 sm:px-6 lg:px-8">
      <div className={`max-w-7xl mx-auto transition-all duration-[var(--duration-slow)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {}
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground-light mb-2">
                Get in Touch
              </h2>
              <p className="text-white text-base sm:text-lg">
                Visit us, call us, or drop us a line. We'd love to hear from you!
              </p>
            </div>

            <div className="space-y-4">
              {}
              <div data-index={0} className="group bg-primary-light rounded-lg p-5 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] border border-primary/10">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-[var(--duration-medium)]">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-foreground-light mb-1">
                      Location
                    </h3>
                    <p className="text-muted text-sm">13710 N Meridian St,Carmel, Indiana</p>
                  </div>
                </div>
              </div>

              {}
              <div data-index={1} className="group bg-secondary-light rounded-lg p-5 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] border border-secondary/10">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center group-hover:bg-secondary/20 transition-colors duration-[var(--duration-medium)]">
                    <Phone className="w-6 h-6 text-secondary-dark" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-foreground-light mb-1">
                      Phone
                    </h3>
                    <Link to="tel:317-740-0001" className="text-foreground text-sm hover:text-secondary-dark transition-colors duration-[var(--duration-fast)]">
                      (317) 740-0001
                    </Link>
                  </div>
                </div>
              </div>

              {}
              <div data-index={2} className="group bg-accent-light rounded-lg p-5 shadow-soft hover:shadow-medium hover:-translate-y-1 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] border border-accent/10">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors duration-[var(--duration-medium)]">
                    <Mail className="w-6 h-6 text-accent-dark" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold text-foreground-light mb-1">
                      Email
                    </h3>
                    <Link to="mailto:Texasmargaritascarmel@gmail.com" className="text-foreground text-sm hover:text-accent-dark transition-colors duration-[var(--duration-fast)] break-all">
                      Texasmargaritascarmel@gmail.com
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-muted-light-background rounded-lg shadow-soft p-6 sm:p-8 border border-border">
            <FormProvider formId="contact-form-details" sectionName="contact-details-form" defaultValues={{
            name: "",
            email: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-light mb-4">
          <Mail className="w-8 h-8 text-secondary-dark" />
        </div>
        <h3 className="text-xl font-display font-semibold text-foreground-light mb-2">
          Message Sent!
        </h3>
        <p className="text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover:scale-[1.02] transition-transform duration-[var(--duration-fast)]">
          Send Another Message
        </Button>
      </div>;
  }
  return <div>
      <h3 className="text-2xl font-display font-bold text-foreground-light mb-6">
        Send Us a Message
      </h3>
      <Form onSubmit={handleSubmit}>
        <div className="space-y-5">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 100,
            message: "Name must be less than 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Your Name" {...field} className="focus:shadow-medium transition-shadow duration-[var(--duration-medium)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="you@example.com" {...field} className="focus:shadow-medium transition-shadow duration-[var(--duration-medium)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          required: "Message is required",
          minLength: {
            value: 10,
            message: "Message must be at least 10 characters"
          },
          maxLength: {
            value: 1000,
            message: "Message must be less than 1000 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Message</FormLabel>
                <FormControl>
                  <Textarea placeholder="Tell us what you're thinking..." className="min-h-32 focus:shadow-medium transition-shadow duration-[var(--duration-medium)]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{submitError}</p>
            </div>}

          <Button type="submit" disabled={isSubmitting} className="w-full hover:scale-[1.02] transition-transform duration-[var(--duration-fast)]">
            {isSubmitting ? <>
                <span className="animate-spin mr-2"></span>
                Sending...
              </> : "Send Message"}
          </Button>
        </div>
      </Form>
    </div>;
}